/**
 * WooCommerce platform adapter
 *
 * Registers the pmw.platform seam: the neutral surface the library core is
 * allowed to call. Everything behind it is WooCommerce/WordPress-specific.
 * A future FluentCart/SureCart harness registers its own object with the
 * same shape (the contract lives in docs/PLATFORM-CONTRACT.md).
 *
 * @since 1.65.0
 */

(function (pmw, $, undefined) {

	/**
	 * Wait until WooCommerce Blocks' wp.hooks global is available.
	 *
	 * @returns {Promise<void>}
	 */
	pmw.wpHooksExists = async () => new Promise(resolve => {
		(function waitForVar() {

			// Resolve if wp.hooks is defined
			if (typeof wp !== "undefined" && typeof wp.hooks !== "undefined") return resolve()
			setTimeout(waitForVar, 50)
		})()
	})

	/**
	 * Load the WooCommerce Blocks hook listeners as a chunk.
	 *
	 * Includes safeguard in case the user changed the default load order for
	 * wp-hooks, or removed it completely.
	 *
	 * @returns {Promise<void>}
	 */
	pmw.loadWcHooksFunctions = async () => {
		await pmw.wpHooksExists()
		await import(/* webpackChunkName: "wc-hooks" */ "./wc_hooks.js")
	}

	/**
	 * Merge the library's built-in element selectors with the ones the shop
	 * added through the pmw_selectors filters, into one selector string.
	 *
	 * Lives in the platform adapter rather than in functions.js because the
	 * event listener files call it while their module body is evaluated, and
	 * functions.js is required later in the boot sequence. Called from a file
	 * that loads before its definition, the call threw
	 * "pmw.prepareSelectors is not a function" and took every listener
	 * registration in that file down with it, silently. The adapter loads
	 * before every listener file, so the ordering holds by construction.
	 *
	 * @since 1.67.1
	 * @param {string[]} selectors Built-in selectors.
	 * @param {string}   type      Selector group, e.g. "addToCart".
	 * @returns {string}
	 */
	pmw.prepareSelectors = (selectors, type) => {

		if (pmwDataLayer?.shop?.selectors?.[type]) {

			// merge the selectors from the addToCartSelectors array with the selectors from the pmwDataLayer.shop.selectors.addToCart array
			selectors = selectors.concat(pmwDataLayer.shop.selectors[type]);

			// Remove duplicates
			selectors = [...new Set(selectors)];

			// If one of the selectors is document, remove it
			if (selectors.indexOf("document") >= 0) {
				selectors.splice(selectors.indexOf("document"), 1);
			}

			// If one of the selectors is body, remove all others
			if (selectors.indexOf("body") >= 0) {
				selectors = ["body"];
			}
		}

		return selectors.join(", ");
	};

	/**
	 * The neutral platform surface consumed by the library core and the
	 * pixel adapters. See docs/PLATFORM-CONTRACT.md.
	 */
	pmw.platform = {

		name: "woocommerce",

		/**
		 * Whether the shop's server-side cart session currently exists.
		 * WooCommerce marks it with the woocommerce_items_in_cart cookie.
		 *
		 * @returns {boolean}
		 */
		cartExists: () => pmw.doesWooCommerceCartExist(),

		/**
		 * Server-side cart content hash, used to detect carts changed
		 * outside client-side tracking. Empty string when unavailable.
		 *
		 * @returns {string}
		 */
		getCartHash: () => pmw.getCookie("woocommerce_cart_hash") || "",

		/**
		 * Product IDs of the products rendered in the current listing, in
		 * DOM order.
		 *
		 * @returns {string[]}
		 */
		getProductIdsOnPage: () => pmw.getProductIdsFromListing(),

		/**
		 * The main product id on a product detail page, or false.
		 *
		 * @returns {string|number|false|undefined}
		 */
		getMainProductId: () => pmw.getMainProductIdFromProductPage(),

		/**
		 * What the shopper has typed into the checkout form so far: the
		 * contact and address fields of the classic checkout and of the
		 * Checkout block, billing first, shipping as the fallback. Every
		 * field is a trimmed string, empty when the field is missing or
		 * blank. Read on demand by pixels whose funnel events need an
		 * identity before an order exists (Triple Whale).
		 *
		 * @returns {{email: string, phone: string, firstName: string, lastName: string, address: {zip: string, city: string, countryCode: string, provinceCode: string}}}
		 */
		getCheckoutContact: () => {

			const read = (selectors) => {
				for (const selector of selectors) {
					const element = document.querySelector(selector);
					if (element && typeof element.value === "string" && element.value.trim()) {
						return element.value.trim();
					}
				}
				return "";
			};

			return {
				email    : read(["#billing_email", "#email"]),
				phone    : read(["#billing_phone", "#billing-phone", "#shipping-phone"]),
				firstName: read(["#billing_first_name", "#billing-first_name", "#shipping-first_name"]),
				lastName : read(["#billing_last_name", "#billing-last_name", "#shipping-last_name"]),
				address  : {
					zip         : read(["#billing_postcode", "#billing-postcode", "#shipping-postcode"]),
					city        : read(["#billing_city", "#billing-city", "#shipping-city"]),
					countryCode : read(["#billing_country", "#billing-country", "#shipping-country"]),
					provinceCode: read(["#billing_state", "#billing-state", "#shipping-state"]),
				},
			};
		},
	};

}(window.pmw = window.pmw || {}, jQuery));
